// SalaryComparisonScreen.js — Full-featured UK Engineering Salary Tool
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Dimensions, SafeAreaView, ActivityIndicator, TextInput,
  Modal, Alert, Share, Animated,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5, MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';

const { width } = Dimensions.get('window');

const ANTHROPIC_API_KEY = 'YOUR_ANTHROPIC_API_KEY';

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────
const SALARY_DATA = {
  'Civil Engineering':          { junior: { min: 25000, max: 32000, avg: 28500 }, mid: { min: 35000, max: 48000, avg: 41500 }, senior: { min: 50000, max: 70000, avg: 60000 }, lead: { min: 65000, max: 90000, avg: 77500 } },
  'Mechanical Engineering':     { junior: { min: 26000, max: 34000, avg: 30000 }, mid: { min: 38000, max: 52000, avg: 45000 }, senior: { min: 55000, max: 75000, avg: 65000 }, lead: { min: 70000, max: 95000, avg: 82500 } },
  'Electrical Engineering':     { junior: { min: 27000, max: 35000, avg: 31000 }, mid: { min: 40000, max: 54000, avg: 47000 }, senior: { min: 58000, max: 78000, avg: 68000 }, lead: { min: 72000, max: 98000, avg: 85000 } },
  'Software Engineering':       { junior: { min: 30000, max: 45000, avg: 37500 }, mid: { min: 50000, max: 70000, avg: 60000 }, senior: { min: 70000, max: 100000, avg: 85000 }, lead: { min: 90000, max: 130000, avg: 110000 } },
  'Chemical Engineering':       { junior: { min: 28000, max: 36000, avg: 32000 }, mid: { min: 42000, max: 56000, avg: 49000 }, senior: { min: 60000, max: 80000, avg: 70000 }, lead: { min: 75000, max: 100000, avg: 87500 } },
  'Aerospace Engineering':      { junior: { min: 29000, max: 38000, avg: 33500 }, mid: { min: 45000, max: 60000, avg: 52500 }, senior: { min: 65000, max: 88000, avg: 76500 }, lead: { min: 80000, max: 110000, avg: 95000 } },
  'Biomedical Engineering':     { junior: { min: 26000, max: 34000, avg: 30000 }, mid: { min: 38000, max: 52000, avg: 45000 }, senior: { min: 55000, max: 75000, avg: 65000 }, lead: { min: 70000, max: 95000, avg: 82500 } },
  'Environmental Engineering':  { junior: { min: 24000, max: 31000, avg: 27500 }, mid: { min: 35000, max: 47000, avg: 41000 }, senior: { min: 48000, max: 68000, avg: 58000 }, lead: { min: 62000, max: 85000, avg: 73500 } },
  'Nuclear Engineering':        { junior: { min: 32000, max: 42000, avg: 37000 }, mid: { min: 48000, max: 65000, avg: 56500 }, senior: { min: 68000, max: 92000, avg: 80000 }, lead: { min: 85000, max: 120000, avg: 102500 } },
  'Structural Engineering':     { junior: { min: 27000, max: 34000, avg: 30500 }, mid: { min: 38000, max: 52000, avg: 45000 }, senior: { min: 54000, max: 72000, avg: 63000 }, lead: { min: 68000, max: 92000, avg: 80000 } },
  'Control & Instrumentation':  { junior: { min: 28000, max: 36000, avg: 32000 }, mid: { min: 40000, max: 55000, avg: 47500 }, senior: { min: 58000, max: 78000, avg: 68000 }, lead: { min: 72000, max: 98000, avg: 85000 } },
  'Marine Engineering':         { junior: { min: 26000, max: 34000, avg: 30000 }, mid: { min: 38000, max: 52000, avg: 45000 }, senior: { min: 55000, max: 74000, avg: 64500 }, lead: { min: 68000, max: 92000, avg: 80000 } },
};

const REGIONAL_DATA = {
  'London':            1.25,
  'South East':        1.15,
  'East of England':   1.10,
  'Scotland':          1.05,
  'South West':        1.08,
  'North West':        1.00,
  'Yorkshire':         0.96,
  'West Midlands':     0.98,
  'East Midlands':     0.97,
  'Wales':             0.95,
  'North East':        0.92,
  'Northern Ireland':  0.93,
};

const SECTOR_MULTIPLIERS = {
  'Private Sector':      1.0,
  'Public Sector':       0.92,
  'Consultancy':         1.12,
  'Contractor':          1.20,
  'Start-up':            1.05,
  'Defence/Government':  1.08,
};

const EXP_LEVELS  = ['junior', 'mid', 'senior', 'lead'];
const EXP_LABELS  = { junior: 'Junior (0-2y)', mid: 'Mid (3-5y)', senior: 'Senior (6-10y)', lead: 'Lead (10+y)' };
const DISC_COLORS = ['#0e4e96', '#19abb5', '#fdb913', '#10b981', '#8b5cf6', '#f59e0b'];

const UK_TAX_BANDS_25_26 = [
  { min: 0,      max: 12570,  rate: 0 },
  { min: 12570,  max: 50270,  rate: 0.20 },
  { min: 50270,  max: 125140, rate: 0.40 },
  { min: 125140, max: Infinity, rate: 0.45 },
];

const calcTax = (gross) => {
  let tax = 0;
  for (const band of UK_TAX_BANDS_25_26) {
    if (gross > band.min) {
      tax += (Math.min(gross, band.max) - band.min) * band.rate;
    }
  }
  return Math.round(tax);
};

const calcNI = (gross) => {
  if (gross <= 12570) return 0;
  if (gross <= 50270) return Math.round((gross - 12570) * 0.12);
  return Math.round(((50270 - 12570) * 0.12) + ((gross - 50270) * 0.02));
};

// AI salary advice
const fetchSalaryAdvice = async (discipline, level, region, salary) => {
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        messages: [{
          role: 'user',
          content: `Give concise UK salary negotiation advice for a ${EXP_LABELS[level]} ${discipline} in ${region} currently earning £${salary?.toLocaleString() || 'unknown'}.
Include: 1) Is their salary competitive? 2) Negotiation tactics (3 bullet points) 3) Skills that command a premium 4) Best time to ask for a raise.
Keep response under 200 words. Be direct and practical.`
        }],
      }),
    });
    const data = await res.json();
    return data.content?.[0]?.text || '';
  } catch {
    return `For a ${EXP_LABELS[level]} ${discipline} in ${region}, focus on demonstrating quantifiable project impact, gaining chartership (CEng/IEng), and researching current market rates on Reed and LinkedIn before negotiations. The best time to raise salary is during annual reviews or when taking on new responsibilities.`;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const AnimatedBar = ({ pct, color, value, delay = 0 }) => {
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, { toValue: pct / 100, duration: 700, delay, useNativeDriver: false }).start();
  }, [pct]);
  return (
    <View style={barStyles.track}>
      <Animated.View style={[barStyles.fill, { width: anim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }), backgroundColor: color }]}>
        <Text style={barStyles.label}>£{Math.round(value).toLocaleString()}</Text>
      </Animated.View>
    </View>
  );
};
const barStyles = StyleSheet.create({
  track: { height: 34, backgroundColor: '#f3f4f6', borderRadius: 8, overflow: 'hidden', marginVertical: 4 },
  fill:  { height: '100%', borderRadius: 8, justifyContent: 'center', paddingHorizontal: 10, minWidth: 60 },
  label: { color: '#fff', fontSize: 12, fontWeight: '700' },
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function SalaryComparisonScreen() {
  const navigation = useNavigation();
  const route      = useRoute();

  const [selectedDisciplines, setSelectedDisciplines] = useState(['Software Engineering', 'Mechanical Engineering']);
  const [selectedRegion,       setSelectedRegion]      = useState('London');
  const [selectedExperience,   setSelectedExperience]  = useState('mid');
  const [selectedSector,       setSelectedSector]      = useState('Private Sector');
  const [comparisonMode,       setComparisonMode]      = useState('discipline'); // discipline | experience | region | sector
  const [salaryPeriod,         setSalaryPeriod]        = useState('annual'); // annual | monthly | daily

  const [showDisciplineModal,  setShowDisciplineModal] = useState(false);
  const [showRegionModal,      setShowRegionModal]     = useState(false);
  const [showSectorModal,      setShowSectorModal]     = useState(false);
  const [showCalculator,       setShowCalculator]      = useState(false);
  const [showAIAdvice,         setShowAIAdvice]        = useState(false);

  const [customSalary,  setCustomSalary]  = useState('');
  const [aiAdvice,      setAiAdvice]      = useState('');
  const [aiLoading,     setAiLoading]     = useState(false);
  const [calcResult,    setCalcResult]    = useState(null);
  const [calcInput,     setCalcInput]     = useState('');

  const disciplines = Object.keys(SALARY_DATA);
  const regions     = Object.keys(REGIONAL_DATA);
  const sectors     = Object.keys(SECTOR_MULTIPLIERS);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const getAdjusted = (disc, exp, region, sector = 'Private Sector') => {
    const base = SALARY_DATA[disc]?.[exp];
    if (!base) return { min: 0, max: 0, avg: 0 };
    const rm = REGIONAL_DATA[region]   || 1;
    const sm = SECTOR_MULTIPLIERS[sector] || 1;
    return { min: Math.round(base.min * rm * sm), max: Math.round(base.max * rm * sm), avg: Math.round(base.avg * rm * sm) };
  };

  const formatSalary = (n) => {
    if (salaryPeriod === 'monthly') return `£${Math.round(n / 12).toLocaleString()}/mo`;
    if (salaryPeriod === 'daily')   return `£${Math.round(n / 260).toLocaleString()}/day`;
    return `£${n.toLocaleString()}`;
  };

  const toggleDiscipline = (d) => {
    if (selectedDisciplines.includes(d)) {
      if (selectedDisciplines.length > 1) setSelectedDisciplines(p => p.filter(x => x !== d));
      else Alert.alert('Notice', 'At least one discipline must be selected');
    } else {
      if (selectedDisciplines.length < 4) setSelectedDisciplines(p => [...p, d]);
      else Alert.alert('Limit', 'Max 4 disciplines can be compared');
    }
  };

  const getDColor = (i) => DISC_COLORS[i % DISC_COLORS.length];

  // ── Summary cards ─────────────────────────────────────────────────────────
  const summaries = selectedDisciplines.map(d => getAdjusted(d, selectedExperience, selectedRegion, selectedSector));
  const avgSalary = Math.round(summaries.reduce((s, x) => s + x.avg, 0) / summaries.length);
  const maxSalary = Math.max(...summaries.map(s => s.max));

  // ── Tax calculator ────────────────────────────────────────────────────────
  const calculateTax = () => {
    const gross = parseInt(calcInput.replace(/,/g, ''), 10);
    if (!gross || gross < 0) { Alert.alert('Invalid', 'Enter a valid salary'); return; }
    const tax  = calcTax(gross);
    const ni   = calcNI(gross);
    const net  = gross - tax - ni;
    setCalcResult({ gross, tax, ni, net, monthly: Math.round(net / 12), effective: Math.round(((tax + ni) / gross) * 100) });
  };

  // ── AI advice ─────────────────────────────────────────────────────────────
  const loadAIAdvice = async () => {
    setAiLoading(true);
    setShowAIAdvice(true);
    const cs = customSalary ? parseInt(customSalary.replace(/,/g, '')) : null;
    const text = await fetchSalaryAdvice(selectedDisciplines[0], selectedExperience, selectedRegion, cs);
    setAiAdvice(text);
    setAiLoading(false);
  };

  // ── Share ─────────────────────────────────────────────────────────────────
  const handleShare = async () => {
    const lines = selectedDisciplines.map((d, i) => {
      const s = getAdjusted(d, selectedExperience, selectedRegion, selectedSector);
      return `• ${d}: £${s.min.toLocaleString()} - £${s.max.toLocaleString()} (avg £${s.avg.toLocaleString()})`;
    });
    await Share.share({
      message: `UK Engineering Salary Comparison\n${EXP_LABELS[selectedExperience]} • ${selectedRegion} • ${selectedSector}\n\n${lines.join('\n')}\n\nShared from JOBENG`,
    });
  };

  // ── Render charts ─────────────────────────────────────────────────────────
  const renderDisciplineChart = () => {
    const maxAvg = Math.max(...selectedDisciplines.map(d => getAdjusted(d, selectedExperience, selectedRegion, selectedSector).avg));
    return (
      <View style={styles.chartCard}>
        <Text style={styles.chartTitle}>Salary Comparison by Discipline</Text>
        <Text style={styles.chartSubtitle}>{EXP_LABELS[selectedExperience]} · {selectedRegion} · {selectedSector}</Text>
        {selectedDisciplines.map((d, i) => {
          const s   = getAdjusted(d, selectedExperience, selectedRegion, selectedSector);
          const pct = (s.avg / maxAvg) * 100;
          return (
            <View key={d} style={styles.barRow}>
              <View style={styles.barLabel}>
                <View style={[styles.colorDot, { backgroundColor: getDColor(i) }]} />
                <Text style={styles.barLabelText} numberOfLines={1}>{d}</Text>
              </View>
              <AnimatedBar pct={pct} color={getDColor(i)} value={s.avg} delay={i * 100} />
              <Text style={styles.barRange}>{formatSalary(s.min)} – {formatSalary(s.max)}</Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderExperienceChart = () => {
    if (selectedDisciplines.length !== 1) return (
      <View style={styles.infoBox}>
        <MaterialIcons name="info-outline" size={18} color="#0e4e96" />
        <Text style={styles.infoText}>Select exactly one discipline to view career progression</Text>
      </View>
    );
    const disc  = selectedDisciplines[0];
    const maxS  = getAdjusted(disc, 'lead', selectedRegion, selectedSector).max;
    return (
      <View style={styles.chartCard}>
        <Text style={styles.chartTitle}>Career Progression</Text>
        <Text style={styles.chartSubtitle}>{disc} · {selectedRegion}</Text>
        {EXP_LEVELS.map((level, i) => {
          const s   = getAdjusted(disc, level, selectedRegion, selectedSector);
          const pct = (s.avg / maxS) * 100;
          return (
            <View key={level} style={styles.barRow}>
              <View style={styles.barLabel}>
                <View style={[styles.colorDot, { backgroundColor: getDColor(i) }]} />
                <Text style={styles.barLabelText}>{EXP_LABELS[level]}</Text>
              </View>
              <AnimatedBar pct={pct} color={getDColor(i)} value={s.avg} delay={i * 100} />
              <Text style={styles.barRange}>{formatSalary(s.min)} – {formatSalary(s.max)}</Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderRegionChart = () => {
    if (selectedDisciplines.length !== 1) return (
      <View style={styles.infoBox}>
        <MaterialIcons name="info-outline" size={18} color="#0e4e96" />
        <Text style={styles.infoText}>Select exactly one discipline to view regional comparison</Text>
      </View>
    );
    const disc = selectedDisciplines[0];
    const maxS = Math.max(...regions.map(r => getAdjusted(disc, selectedExperience, r, selectedSector).avg));
    return (
      <View style={styles.chartCard}>
        <Text style={styles.chartTitle}>Regional Comparison</Text>
        <Text style={styles.chartSubtitle}>{disc} · {EXP_LABELS[selectedExperience]}</Text>
        {regions.map((region, i) => {
          const s   = getAdjusted(disc, selectedExperience, region, selectedSector);
          const pct = (s.avg / maxS) * 100;
          const mult = REGIONAL_DATA[region] || 1;
          return (
            <View key={region} style={styles.barRow}>
              <View style={styles.barLabel}>
                <View style={[styles.colorDot, { backgroundColor: getDColor(i) }]} />
                <Text style={styles.barLabelText}>{region}</Text>
              </View>
              <AnimatedBar pct={pct} color={getDColor(i)} value={s.avg} delay={i * 80} />
              <Text style={styles.barRange}>
                {mult > 1 ? '+' : ''}{((mult - 1) * 100).toFixed(0)}% vs national
              </Text>
            </View>
          );
        })}
      </View>
    );
  };

  const renderSectorChart = () => {
    if (selectedDisciplines.length !== 1) return (
      <View style={styles.infoBox}>
        <MaterialIcons name="info-outline" size={18} color="#0e4e96" />
        <Text style={styles.infoText}>Select exactly one discipline to view sector comparison</Text>
      </View>
    );
    const disc = selectedDisciplines[0];
    const maxS = Math.max(...sectors.map(s => getAdjusted(disc, selectedExperience, selectedRegion, s).avg));
    return (
      <View style={styles.chartCard}>
        <Text style={styles.chartTitle}>Sector Comparison</Text>
        <Text style={styles.chartSubtitle}>{disc} · {EXP_LABELS[selectedExperience]} · {selectedRegion}</Text>
        {sectors.map((sector, i) => {
          const s   = getAdjusted(disc, selectedExperience, selectedRegion, sector);
          const pct = (s.avg / maxS) * 100;
          const mult = SECTOR_MULTIPLIERS[sector] || 1;
          return (
            <View key={sector} style={styles.barRow}>
              <View style={styles.barLabel}>
                <View style={[styles.colorDot, { backgroundColor: getDColor(i) }]} />
                <Text style={styles.barLabelText}>{sector}</Text>
              </View>
              <AnimatedBar pct={pct} color={getDColor(i)} value={s.avg} delay={i * 80} />
              <Text style={styles.barRange}>avg {formatSalary(s.avg)}</Text>
            </View>
          );
        })}
      </View>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.headerBtn} onPress={() => navigation.goBack()}>
          <MaterialIcons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <Text style={styles.headerTitle}>Salary Comparison</Text>
          <Text style={styles.headerSubtitle}>UK Engineering · {new Date().getFullYear()}</Text>
        </View>
        <TouchableOpacity style={styles.headerBtn} onPress={handleShare}>
          <MaterialIcons name="share" size={22} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>

        {/* Mode tabs */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.modeScroll} contentContainerStyle={{ paddingHorizontal: 12 }}>
          {[
            { id: 'discipline', label: 'By Discipline', icon: 'briefcase-outline' },
            { id: 'experience', label: 'By Experience', icon: 'stairs-up' },
            { id: 'region',     label: 'By Region',     icon: 'map-marker-outline' },
            { id: 'sector',     label: 'By Sector',     icon: 'domain' },
          ].map(m => (
            <TouchableOpacity key={m.id}
              style={[styles.modeTab, comparisonMode === m.id && styles.modeTabActive]}
              onPress={() => setComparisonMode(m.id)}>
              <MaterialCommunityIcons name={m.icon} size={14} color={comparisonMode === m.id ? '#fff' : '#6b7280'} />
              <Text style={[styles.modeTabText, comparisonMode === m.id && styles.modeTabTextActive]}>{m.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Summary cards */}
        <View style={styles.summaryRow}>
          <View style={styles.summaryCard}>
            <MaterialCommunityIcons name="chart-line" size={22} color="#0e4e96" />
            <Text style={styles.summaryValue}>{formatSalary(avgSalary)}</Text>
            <Text style={styles.summaryLabel}>Average</Text>
          </View>
          <View style={styles.summaryCard}>
            <MaterialCommunityIcons name="arrow-up-bold" size={22} color="#10b981" />
            <Text style={[styles.summaryValue, { color: '#10b981' }]}>{formatSalary(maxSalary)}</Text>
            <Text style={styles.summaryLabel}>Top Salary</Text>
          </View>
          <View style={styles.summaryCard}>
            <MaterialCommunityIcons name="map-marker" size={22} color="#f59e0b" />
            <Text style={[styles.summaryValue, { color: '#f59e0b', fontSize: 13 }]}>{selectedRegion}</Text>
            <Text style={styles.summaryLabel}>Region</Text>
          </View>
        </View>

        {/* Period toggle */}
        <View style={styles.periodRow}>
          <Text style={styles.periodLabel}>Show as:</Text>
          {[
            { id: 'annual',  label: 'Annual' },
            { id: 'monthly', label: 'Monthly' },
            { id: 'daily',   label: 'Daily' },
          ].map(p => (
            <TouchableOpacity key={p.id} style={[styles.periodBtn, salaryPeriod === p.id && styles.periodBtnActive]}
              onPress={() => setSalaryPeriod(p.id)}>
              <Text style={[styles.periodBtnText, salaryPeriod === p.id && styles.periodBtnTextActive]}>{p.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Filters section */}
        <View style={styles.filtersCard}>
          <Text style={styles.filtersTitle}>Filters</Text>

          {/* Discipline selector */}
          <TouchableOpacity style={styles.filterRow} onPress={() => setShowDisciplineModal(true)}>
            <MaterialCommunityIcons name="briefcase-outline" size={18} color="#0e4e96" />
            <View style={{ flex: 1 }}>
              <Text style={styles.filterRowLabel}>Disciplines ({selectedDisciplines.length}/4)</Text>
              <Text style={styles.filterRowValue} numberOfLines={1}>{selectedDisciplines.join(', ')}</Text>
            </View>
            <MaterialIcons name="chevron-right" size={20} color="#9ca3af" />
          </TouchableOpacity>

          {/* Experience */}
          {comparisonMode !== 'experience' && (
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionLabel}>Experience Level</Text>
              <View style={styles.expRow}>
                {EXP_LEVELS.map(l => (
                  <TouchableOpacity key={l} style={[styles.expChip, selectedExperience === l && styles.expChipActive]}
                    onPress={() => setSelectedExperience(l)}>
                    <Text style={[styles.expChipText, selectedExperience === l && styles.expChipTextActive]}>
                      {l.charAt(0).toUpperCase() + l.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {/* Region selector */}
          {comparisonMode !== 'region' && (
            <TouchableOpacity style={styles.filterRow} onPress={() => setShowRegionModal(true)}>
              <MaterialCommunityIcons name="map-marker-outline" size={18} color="#0e4e96" />
              <View style={{ flex: 1 }}>
                <Text style={styles.filterRowLabel}>Region</Text>
                <Text style={styles.filterRowValue}>{selectedRegion} · {((REGIONAL_DATA[selectedRegion] - 1) * 100) > 0 ? '+' : ''}{((REGIONAL_DATA[selectedRegion] - 1) * 100).toFixed(0)}% modifier</Text>
              </View>
              <MaterialIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          )}

          {/* Sector selector */}
          {comparisonMode !== 'sector' && (
            <TouchableOpacity style={styles.filterRow} onPress={() => setShowSectorModal(true)}>
              <MaterialCommunityIcons name="domain" size={18} color="#0e4e96" />
              <View style={{ flex: 1 }}>
                <Text style={styles.filterRowLabel}>Sector</Text>
                <Text style={styles.filterRowValue}>{selectedSector}</Text>
              </View>
              <MaterialIcons name="chevron-right" size={20} color="#9ca3af" />
            </TouchableOpacity>
          )}
        </View>

        {/* Chart */}
        {comparisonMode === 'discipline'  && renderDisciplineChart()}
        {comparisonMode === 'experience'  && renderExperienceChart()}
        {comparisonMode === 'region'      && renderRegionChart()}
        {comparisonMode === 'sector'      && renderSectorChart()}

        {/* Custom salary benchmark */}
        <View style={styles.benchmarkCard}>
          <Text style={styles.benchmarkTitle}>📊 Benchmark Your Salary</Text>
          <Text style={styles.benchmarkSubtitle}>Compare your current salary to the market average</Text>
          <View style={styles.benchmarkInput}>
            <Text style={styles.benchmarkCurrency}>£</Text>
            <TextInput
              style={styles.benchmarkField}
              placeholder="Your current salary"
              value={customSalary}
              onChangeText={setCustomSalary}
              keyboardType="numeric"
              placeholderTextColor="#9ca3af"
            />
          </View>
          {customSalary ? (
            <View style={styles.benchmarkResult}>
              {(() => {
                const cs   = parseInt(customSalary.replace(/,/g, ''), 10) || 0;
                const avg  = getAdjusted(selectedDisciplines[0], selectedExperience, selectedRegion, selectedSector).avg;
                const diff = cs - avg;
                const pct  = Math.round((diff / avg) * 100);
                const color = diff >= 0 ? '#10b981' : '#ef4444';
                return (
                  <>
                    <View style={styles.benchmarkRow}>
                      <Text style={styles.benchmarkRowLabel}>Your salary</Text>
                      <Text style={[styles.benchmarkRowValue, { color: '#1f2937' }]}>£{cs.toLocaleString()}</Text>
                    </View>
                    <View style={styles.benchmarkRow}>
                      <Text style={styles.benchmarkRowLabel}>Market avg ({selectedDisciplines[0]})</Text>
                      <Text style={styles.benchmarkRowValue}>£{avg.toLocaleString()}</Text>
                    </View>
                    <View style={[styles.benchmarkDiff, { backgroundColor: color + '22' }]}>
                      <MaterialCommunityIcons name={diff >= 0 ? 'trending-up' : 'trending-down'} size={18} color={color} />
                      <Text style={[styles.benchmarkDiffText, { color }]}>
                        {diff >= 0 ? '+' : ''}£{Math.abs(diff).toLocaleString()} ({pct}%) {diff >= 0 ? 'above' : 'below'} market average
                      </Text>
                    </View>
                  </>
                );
              })()}
            </View>
          ) : null}
          <TouchableOpacity style={styles.aiAdviceBtn} onPress={loadAIAdvice}>
            <MaterialCommunityIcons name="brain" size={16} color="#fff" />
            <Text style={styles.aiAdviceBtnText}>Get AI Negotiation Advice</Text>
          </TouchableOpacity>
        </View>

        {/* Tax calculator */}
        <View style={styles.calcCard}>
          <TouchableOpacity style={styles.calcHeader} onPress={() => setShowCalculator(v => !v)}>
            <View style={styles.calcHeaderLeft}>
              <MaterialCommunityIcons name="calculator-variant" size={20} color="#0e4e96" />
              <Text style={styles.calcTitle}>UK Tax & Take-Home Calculator</Text>
            </View>
            <MaterialIcons name={showCalculator ? 'expand-less' : 'expand-more'} size={22} color="#0e4e96" />
          </TouchableOpacity>
          {showCalculator && (
            <View style={styles.calcBody}>
              <Text style={styles.calcSubtitle}>2025/26 Tax Year · England, Wales & NI</Text>
              <View style={styles.calcInputRow}>
                <Text style={styles.calcCurrencySign}>£</Text>
                <TextInput
                  style={styles.calcInput}
                  placeholder="Enter gross annual salary"
                  value={calcInput}
                  onChangeText={setCalcInput}
                  keyboardType="numeric"
                  placeholderTextColor="#9ca3af"
                />
                <TouchableOpacity style={styles.calcBtn} onPress={calculateTax}>
                  <Text style={styles.calcBtnText}>Calculate</Text>
                </TouchableOpacity>
              </View>
              {calcResult && (
                <View style={styles.calcResults}>
                  {[
                    { label: 'Gross Salary',          value: calcResult.gross,    color: '#1f2937' },
                    { label: 'Income Tax',             value: -calcResult.tax,     color: '#ef4444' },
                    { label: 'National Insurance',     value: -calcResult.ni,      color: '#f59e0b' },
                    { label: 'Net Take-Home (Annual)', value: calcResult.net,      color: '#10b981' },
                    { label: 'Monthly Take-Home',      value: calcResult.monthly,  color: '#0e4e96' },
                  ].map((r, i) => (
                    <View key={i} style={[styles.calcRow, i === 3 && styles.calcRowTotal]}>
                      <Text style={styles.calcRowLabel}>{r.label}</Text>
                      <Text style={[styles.calcRowValue, { color: r.color }]}>
                        {r.value < 0 ? '-' : ''}£{Math.abs(r.value).toLocaleString()}
                      </Text>
                    </View>
                  ))}
                  <View style={styles.calcEffRate}>
                    <Text style={styles.calcEffRateText}>Effective tax rate: {calcResult.effective}%</Text>
                  </View>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Key insights */}
        <View style={styles.insightsCard}>
          <Text style={styles.insightsTitle}>Key Insights</Text>
          {[
            { icon: 'trending-up', color: '#10b981', text: 'Software Engineering offers the highest average salaries across all experience levels in the UK.' },
            { icon: 'map-marker',  color: '#0e4e96', text: `London salaries are ${((REGIONAL_DATA['London'] - 1) * 100).toFixed(0)}% above the UK national average.` },
            { icon: 'domain',      color: '#8b5cf6', text: 'Contractors earn ~20% more than permanent employees, but without benefits or pension contributions.' },
            { icon: 'certificate', color: '#fdb913', text: 'Chartered Engineers (CEng) typically command a 15-25% salary premium over unchartered peers.' },
            { icon: 'school',      color: '#f59e0b', text: 'A relevant Masters degree can increase starting salaries by 10-15% for graduate engineers.' },
          ].map((ins, i) => (
            <View key={i} style={styles.insightRow}>
              <View style={[styles.insightIcon, { backgroundColor: ins.color + '22' }]}>
                <MaterialCommunityIcons name={ins.icon} size={18} color={ins.color} />
              </View>
              <Text style={styles.insightText}>{ins.text}</Text>
            </View>
          ))}
        </View>

        <View style={styles.dataSource}>
          <Text style={styles.dataSourceText}>Data: ONS ASHE 2024, Reed, LinkedIn, Industry Surveys · Updated monthly</Text>
        </View>
        <View style={{ height: 30 }} />
      </ScrollView>

      {/* Discipline modal */}
      <Modal visible={showDisciplineModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Disciplines (max 4)</Text>
              <TouchableOpacity onPress={() => setShowDisciplineModal(false)}>
                <MaterialIcons name="close" size={24} color="#6b7280" />
              </TouchableOpacity>
            </View>
            <ScrollView>
              {disciplines.map(d => (
                <TouchableOpacity key={d} style={[styles.modalItem, selectedDisciplines.includes(d) && styles.modalItemActive]}
                  onPress={() => toggleDiscipline(d)}>
                  <Text style={[styles.modalItemText, selectedDisciplines.includes(d) && styles.modalItemTextActive]}>{d}</Text>
                  {selectedDisciplines.includes(d) && <MaterialIcons name="check" size={20} color="#0e4e96" />}
                </TouchableOpacity>
              ))}
            </ScrollView>
            <View style={styles.modalFooter}>
              <Text style={styles.modalFooterText}>{selectedDisciplines.length}/4 selected</Text>
              <TouchableOpacity style={styles.modalDoneBtn} onPress={() => setShowDisciplineModal(false)}>
                <Text style={styles.modalDoneText}>Done</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Region modal */}
      <Modal visible={showRegionModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Region</Text>
              <TouchableOpacity onPress={() => setShowRegionModal(false)}>
                <MaterialIcons name="close" size={24} color="#6b7280" />
              </TouchableOpacity>
            </View>
            <ScrollView>
              {regions.map(r => {
                const mult = REGIONAL_DATA[r] || 1;
                const label = mult > 1 ? `+${((mult - 1) * 100).toFixed(0)}%` : `${((mult - 1) * 100).toFixed(0)}%`;
                return (
                  <TouchableOpacity key={r} style={[styles.modalItem, selectedRegion === r && styles.modalItemActive]}
                    onPress={() => { setSelectedRegion(r); setShowRegionModal(false); }}>
                    <Text style={[styles.modalItemText, selectedRegion === r && styles.modalItemTextActive]}>{r}</Text>
                    <Text style={{ fontSize: 12, color: mult >= 1 ? '#10b981' : '#ef4444', fontWeight: '600' }}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Sector modal */}
      <Modal visible={showSectorModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Sector</Text>
              <TouchableOpacity onPress={() => setShowSectorModal(false)}>
                <MaterialIcons name="close" size={24} color="#6b7280" />
              </TouchableOpacity>
            </View>
            <ScrollView>
              {sectors.map(s => {
                const mult = SECTOR_MULTIPLIERS[s] || 1;
                const label = mult > 1 ? `+${((mult - 1) * 100).toFixed(0)}%` : `${((mult - 1) * 100).toFixed(0)}%`;
                return (
                  <TouchableOpacity key={s} style={[styles.modalItem, selectedSector === s && styles.modalItemActive]}
                    onPress={() => { setSelectedSector(s); setShowSectorModal(false); }}>
                    <Text style={[styles.modalItemText, selectedSector === s && styles.modalItemTextActive]}>{s}</Text>
                    <Text style={{ fontSize: 12, color: mult >= 1 ? '#10b981' : '#ef4444', fontWeight: '600' }}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* AI Advice modal */}
      <Modal visible={showAIAdvice} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalSheet, { maxHeight: '70%' }]}>
            <View style={styles.modalHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <MaterialCommunityIcons name="brain" size={20} color="#0e4e96" />
                <Text style={styles.modalTitle}>AI Salary Advice</Text>
              </View>
              <TouchableOpacity onPress={() => setShowAIAdvice(false)}>
                <MaterialIcons name="close" size={24} color="#6b7280" />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ padding: 20 }}>
              {aiLoading ? (
                <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                  <ActivityIndicator size={32} color="#0e4e96" />
                  <Text style={{ marginTop: 12, color: '#6b7280' }}>Generating advice with Claude AI...</Text>
                </View>
              ) : (
                <>
                  <View style={styles.aiContextRow}>
                    {[selectedDisciplines[0], EXP_LABELS[selectedExperience], selectedRegion].map((b, i) => (
                      <View key={i} style={styles.aiContextBadge}>
                        <Text style={styles.aiContextText}>{b}</Text>
                      </View>
                    ))}
                  </View>
                  <Text style={styles.aiAdviceText}>{aiAdvice}</Text>
                  <TouchableOpacity style={styles.aiRegenBtn} onPress={loadAIAdvice}>
                    <MaterialCommunityIcons name="refresh" size={14} color="#0e4e96" />
                    <Text style={styles.aiRegenText}>Regenerate</Text>
                  </TouchableOpacity>
                </>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container:         { flex: 1, backgroundColor: '#f8fafc' },
  header:            { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  headerBtn:         { padding: 6 },
  headerCenter:      { flex: 1, marginLeft: 8 },
  headerTitle:       { fontSize: 18, fontWeight: '700', color: '#1f2937' },
  headerSubtitle:    { fontSize: 11, color: '#9ca3af' },
  // Mode tabs
  modeScroll:        { backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', maxHeight: 50 },
  modeTab:           { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10, marginHorizontal: 4, borderRadius: 20, backgroundColor: '#f3f4f6', gap: 5 },
  modeTabActive:     { backgroundColor: '#0e4e96' },
  modeTabText:       { fontSize: 12, fontWeight: '600', color: '#6b7280' },
  modeTabTextActive: { color: '#fff' },
  // Summary
  summaryRow:        { flexDirection: 'row', padding: 12, gap: 8 },
  summaryCard:       { flex: 1, backgroundColor: '#fff', borderRadius: 12, padding: 14, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  summaryValue:      { fontSize: 16, fontWeight: '700', color: '#1f2937', marginTop: 6, marginBottom: 2 },
  summaryLabel:      { fontSize: 11, color: '#9ca3af' },
  // Period
  periodRow:         { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 8, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 8 },
  periodLabel:       { fontSize: 12, color: '#6b7280', fontWeight: '500' },
  periodBtn:         { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14, backgroundColor: '#f3f4f6' },
  periodBtnActive:   { backgroundColor: '#0e4e96' },
  periodBtnText:     { fontSize: 12, color: '#6b7280', fontWeight: '500' },
  periodBtnTextActive: { color: '#fff', fontWeight: '700' },
  // Filters
  filtersCard:       { margin: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  filtersTitle:      { fontSize: 14, fontWeight: '700', color: '#1f2937', marginBottom: 12 },
  filterRow:         { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f3f4f6', gap: 10 },
  filterRowLabel:    { fontSize: 12, color: '#9ca3af', marginBottom: 2 },
  filterRowValue:    { fontSize: 14, fontWeight: '600', color: '#1f2937' },
  filterSection:     { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  filterSectionLabel:{ fontSize: 12, color: '#9ca3af', marginBottom: 8 },
  expRow:            { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  expChip:           { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, backgroundColor: '#f3f4f6' },
  expChipActive:     { backgroundColor: '#0e4e96' },
  expChipText:       { fontSize: 12, color: '#6b7280', fontWeight: '500' },
  expChipTextActive: { color: '#fff', fontWeight: '700' },
  // Chart
  chartCard:         { margin: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  chartTitle:        { fontSize: 16, fontWeight: '700', color: '#1f2937', marginBottom: 4 },
  chartSubtitle:     { fontSize: 12, color: '#9ca3af', marginBottom: 16 },
  barRow:            { marginBottom: 12 },
  barLabel:          { flexDirection: 'row', alignItems: 'center', marginBottom: 4, gap: 6 },
  colorDot:          { width: 10, height: 10, borderRadius: 5 },
  barLabelText:      { fontSize: 12, color: '#374151', fontWeight: '500', flex: 1 },
  barRange:          { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  infoBox:           { flexDirection: 'row', alignItems: 'center', margin: 12, backgroundColor: '#f0f4ff', borderRadius: 12, padding: 14, gap: 10 },
  infoText:          { flex: 1, fontSize: 13, color: '#374151' },
  // Benchmark
  benchmarkCard:     { margin: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  benchmarkTitle:    { fontSize: 16, fontWeight: '700', color: '#1f2937', marginBottom: 4 },
  benchmarkSubtitle: { fontSize: 12, color: '#9ca3af', marginBottom: 14 },
  benchmarkInput:    { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, marginBottom: 12, height: 48 },
  benchmarkCurrency: { paddingHorizontal: 14, fontSize: 16, color: '#9ca3af', fontWeight: '600' },
  benchmarkField:    { flex: 1, fontSize: 15, color: '#1f2937' },
  benchmarkResult:   { marginBottom: 14 },
  benchmarkRow:      { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  benchmarkRowLabel: { fontSize: 13, color: '#6b7280' },
  benchmarkRowValue: { fontSize: 13, fontWeight: '600', color: '#1f2937' },
  benchmarkDiff:     { flexDirection: 'row', alignItems: 'center', borderRadius: 10, padding: 10, gap: 8, marginTop: 8 },
  benchmarkDiffText: { fontSize: 13, fontWeight: '600', flex: 1 },
  aiAdviceBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 12, gap: 8 },
  aiAdviceBtnText:   { color: '#fff', fontWeight: '700', fontSize: 14 },
  // Calculator
  calcCard:          { margin: 12, backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  calcHeader:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16 },
  calcHeaderLeft:    { flexDirection: 'row', alignItems: 'center', gap: 8 },
  calcTitle:         { fontSize: 15, fontWeight: '700', color: '#1f2937' },
  calcBody:          { paddingHorizontal: 16, paddingBottom: 16 },
  calcSubtitle:      { fontSize: 11, color: '#9ca3af', marginBottom: 12 },
  calcInputRow:      { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
  calcCurrencySign:  { fontSize: 16, color: '#9ca3af', fontWeight: '600' },
  calcInput:         { flex: 1, borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, paddingHorizontal: 12, height: 44, fontSize: 15, color: '#1f2937' },
  calcBtn:           { backgroundColor: '#0e4e96', paddingHorizontal: 16, paddingVertical: 12, borderRadius: 10 },
  calcBtnText:       { color: '#fff', fontWeight: '700', fontSize: 13 },
  calcResults:       { backgroundColor: '#f9fafb', borderRadius: 10, padding: 14 },
  calcRow:           { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  calcRowTotal:      { borderTopWidth: 2, borderTopColor: '#0e4e96', borderBottomWidth: 0, marginTop: 4, paddingTop: 12 },
  calcRowLabel:      { fontSize: 13, color: '#6b7280' },
  calcRowValue:      { fontSize: 14, fontWeight: '700' },
  calcEffRate:       { alignItems: 'center', marginTop: 10 },
  calcEffRateText:   { fontSize: 12, color: '#9ca3af' },
  // Insights
  insightsCard:      { margin: 12, backgroundColor: '#fff', borderRadius: 14, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1 },
  insightsTitle:     { fontSize: 16, fontWeight: '700', color: '#1f2937', marginBottom: 14 },
  insightRow:        { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 12, gap: 10 },
  insightIcon:       { width: 34, height: 34, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },
  insightText:       { flex: 1, fontSize: 13, color: '#374151', lineHeight: 19 },
  dataSource:        { margin: 12, alignItems: 'center' },
  dataSourceText:    { fontSize: 11, color: '#9ca3af', textAlign: 'center' },
  // Modals
  modalOverlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalSheet:        { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '80%' },
  modalHeader:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  modalTitle:        { fontSize: 17, fontWeight: '700', color: '#1f2937' },
  modalItem:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f9fafb' },
  modalItemActive:   { backgroundColor: '#f0f4ff' },
  modalItemText:     { fontSize: 14, color: '#374151' },
  modalItemTextActive:{ fontWeight: '700', color: '#0e4e96' },
  modalFooter:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderTopWidth: 1, borderTopColor: '#e5e7eb' },
  modalFooterText:   { fontSize: 13, color: '#6b7280' },
  modalDoneBtn:      { backgroundColor: '#0e4e96', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10 },
  modalDoneText:     { color: '#fff', fontWeight: '700', fontSize: 14 },
  // AI modal
  aiContextRow:      { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  aiContextBadge:    { backgroundColor: '#e8f0fe', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 14 },
  aiContextText:     { fontSize: 11, color: '#0e4e96', fontWeight: '600' },
  aiAdviceText:      { fontSize: 14, color: '#374151', lineHeight: 22 },
  aiRegenBtn:        { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 16, alignSelf: 'flex-end' },
  aiRegenText:       { fontSize: 12, color: '#0e4e96', fontWeight: '600' },
});
